import React, { LegacyRef, useContext } from 'react';
import t from 'react-translate';
import { css } from '@emotion/core';
import { useDispatch, useSelector } from 'react-redux';

// redux
import { wrapThunkAction } from 'redux/utils';
import { addTableColumn } from 'redux/actions/quizzes';
import { getFlatCourseAliases } from 'redux/selectors/course';
import { ComponentType } from 'redux/schemas/models/lecture-component';

import NvTooltip from 'shared/components/nv-tooltip';
import QuestionSetContext, { SavingIndicator } from 'lecture_pages/services/question-set-context';
import NvIcon from 'shared/components/nv-icon';
import { quarterSpacing, standardSpacing } from 'styles/global_defaults/scaffolding';
import { gray4, gray6, highTide, primary, white } from 'styles/global_defaults/colors';
import useQuestionSetHelpers from 'quizzes/hooks/use-question-set-helpers';

type AddColumnButtonProps = {
  addColumnRef: LegacyRef<HTMLButtonElement>
  isAddColumnDisabled: boolean;
  setIsAddColumnDisabled: Function;
};
const AddColumnButton = (props: AddColumnButtonProps) => {
  const { addColumnRef, isAddColumnDisabled, setIsAddColumnDisabled } = props;
  const {
    currentQuestion,
    savingIndicatorTimeoutRef,
    setSavingStatus,
    canMakeStructuralChanges,
  } = useContext(QuestionSetContext);

  const { dataQaQuestionSetAttributes, isCurrentActivityType } = useQuestionSetHelpers();
  const { numColumns } = currentQuestion;
  const isMaxColumnLimitReached = numColumns === 6;
  const dispatch = useDispatch();
  const courseAliases = useSelector(getFlatCourseAliases);

  const isButtonDisabled = isAddColumnDisabled
    || isMaxColumnLimitReached
    || !canMakeStructuralChanges;

  const buttonStyles = css`
    &.add-column-button {
      position: absolute;
      left: 100%;
      transform: translateX(5px);
      background-color: ${isButtonDisabled ? gray6 : primary};
      color: ${isButtonDisabled ? gray4 : white};
      border: none;
      border-radius: ${quarterSpacing}px;
      width: ${standardSpacing}px;
    }
    &.add-column-button:hover {
      background-color: ${isButtonDisabled ? gray6 : highTide};
    }
  `;

  const handleAddColumn = () => {
    setIsAddColumnDisabled(true);
    setSavingStatus(SavingIndicator.SAVING_STATUS);
    clearTimeout(savingIndicatorTimeoutRef.current);
    wrapThunkAction(dispatch(addTableColumn(
      {
        questionId: currentQuestion.id,
        columnIndex: numColumns - 1,
      },
    ))).then((res) => {
      setSavingStatus(SavingIndicator.SUCCESS_STATUS);
    }).catch(() => {
      setSavingStatus(SavingIndicator.ERROR_STATUS);
    }).finally(() => {
      setIsAddColumnDisabled(false);
      savingIndicatorTimeoutRef.current = setTimeout(() => {
        setSavingStatus(SavingIndicator.HIDDEN_STATUS);
      }, 2000);
    });
  };

  const getTooltipText = () => {
    if (!canMakeStructuralChanges) {
      if (isCurrentActivityType(ComponentType.SURVEY)) {
        return t.SURVEYS.TABLE_QUESTION.DISABLED_TOOLTIP(courseAliases);
      }
      return t.QUIZZES.TABLE_QUESTION.DISABLED_TOOLTIP(courseAliases);
    }
    if (isMaxColumnLimitReached) {
      return t.QUIZZES.TABLE_QUESTION.MAX_COLUMN_REACHED();
    }

    return t.QUIZZES.TABLE_QUESTION.ADD_COLUMN();
  };

  return (
    <NvTooltip
      text={getTooltipText()}
    >
      <button
        css={buttonStyles}
        type='button'
        onClick={handleAddColumn}
        ref={addColumnRef}
        disabled={isButtonDisabled}
        className='add-column-button d-flex align-items-center justify-content-center'
        data-qa={dataQaQuestionSetAttributes.questionModal.tableQuestion.addColumn}
      >
        <NvIcon
          size='xs-smallest'
          icon='create-new-post'
        />
      </button>
    </NvTooltip>
  );
};

export default AddColumnButton;
